import { CheckCircle2, XCircle, Phone } from "lucide-react";
import { PHONE_DISPLAY, PHONE_HREF } from "@/lib/contact";

const ACCEPT = [
    "General and household waste",
    "Green and garden waste",
    "Mixed heavy waste (soil, bricks, concrete)",
    "Construction waste",
    "Commercial waste",
];

const REJECT = [
    "Asbestos",
    "Chemicals",
    "Food waste",
    "General garbage",
];

export const WhatWeTake = () => {
    return (
        <section
            id="what-we-take"
            data-testid="section-what-we-take"
            className="py-14 md:py-20 bg-white border-y border-tp-forest/10"
        >
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="max-w-2xl tp-reveal">
                    <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-tp-forest font-semibold">
                        <span className="h-1.5 w-1.5 rounded-full bg-tp-gold" />
                        What goes in the bin
                    </div>
                    <h2 className="mt-3 font-heading text-3xl md:text-4xl text-tp-forest leading-tight">
                        Simple rules — what we take and what we don’t
                    </h2>
                    <p className="mt-3 text-sm md:text-base text-tp-ink/80">
                        We keep it easy. Tick list of accepted items below — and a clear list
                        of the few things we can’t take, for safety and compliance reasons.
                    </p>
                </div>

                <div className="mt-8 md:mt-10 grid gap-5 md:gap-6 md:grid-cols-2">
                    {/* Accepted */}
                    <article
                        data-testid="what-we-take-panel"
                        className="tp-reveal bg-tp-offwhite rounded-2xl p-5 md:p-6 border-l-4 border-l-tp-gold shadow-[0_10px_30px_rgba(20,83,45,0.06)]"
                    >
                        <div className="flex items-center gap-2">
                            <span className="inline-flex items-center justify-center h-8 w-8 rounded-lg bg-white ring-1 ring-tp-recycle/40">
                                <CheckCircle2 size={18} strokeWidth={2.4} className="text-tp-recycle" />
                            </span>
                            <h3 className="font-heading text-2xl text-tp-forest">We accept</h3>
                        </div>
                        <ul className="mt-4 space-y-3">
                            {ACCEPT.map((item) => (
                                <li
                                    key={item}
                                    className="flex items-start gap-2 text-sm md:text-base text-tp-ink"
                                >
                                    <CheckCircle2
                                        size={18}
                                        strokeWidth={2.4}
                                        className="text-tp-recycle shrink-0 mt-0.5"
                                    />
                                    <span>{item}</span>
                                </li>
                            ))}
                        </ul>
                    </article>

                    {/* Rejected */}
                    <article
                        data-testid="what-we-dont-take-panel"
                        className="tp-reveal bg-tp-offwhite rounded-2xl p-5 md:p-6 border-l-4 border-l-tp-forest shadow-[0_10px_30px_rgba(20,83,45,0.06)]"
                    >
                        <div className="flex items-center gap-2">
                            <span className="inline-flex items-center justify-center h-8 w-8 rounded-lg bg-white ring-1 ring-tp-forest/20">
                                <XCircle size={18} strokeWidth={2.4} className="text-tp-forest" />
                            </span>
                            <h3 className="font-heading text-2xl text-tp-forest">We don’t accept</h3>
                        </div>
                        <ul className="mt-4 space-y-3">
                            {REJECT.map((item) => (
                                <li
                                    key={item}
                                    className="flex items-start gap-2 text-sm md:text-base text-tp-ink"
                                >
                                    <XCircle
                                        size={18}
                                        strokeWidth={2.4}
                                        className="text-tp-forest/80 shrink-0 mt-0.5"
                                    />
                                    <span>{item}</span>
                                </li>
                            ))}
                        </ul>
                        <p className="mt-4 text-xs text-tp-ink/70">
                            Unsure whether your waste is accepted? Give us a call and we’ll
                            let you know before delivery.
                        </p>
                    </article>
                </div>

                <div className="mt-6 md:mt-8 rounded-2xl bg-tp-forest text-white p-5 md:p-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                    <div>
                        <div className="text-xs uppercase tracking-[0.16em] text-tp-gold">Not sure?</div>
                        <p className="mt-1 font-heading text-xl md:text-2xl">
                            Give us a call and we’ll sort it in a minute.
                        </p>
                    </div>
                    <a
                        href={PHONE_HREF}
                        data-testid="what-we-take-call"
                        className="tp-btn-primary text-sm h-11 tp-focus"
                    >
                        <Phone size={16} strokeWidth={2.6} />
                        Call {PHONE_DISPLAY}
                    </a>
                </div>
            </div>
        </section>
    );
};

export default WhatWeTake;
